import {
  getTechnologyDefinition,
  isTechnologyId,
  type TechnologyId,
} from '../data/technologies'
import type { ActiveMajorPowerId } from '../data/regions'
import { ACTIVE_MAJOR_POWER_IDS } from '../data/nations'

export interface ActiveResearchProject {
  technologyId: TechnologyId
  progress: number
}

export interface NationResearchState {
  completed: TechnologyId[]
  active: ActiveResearchProject | null
}

export type NationResearchRecord = Record<ActiveMajorPowerId, NationResearchState>

export type ResearchBlockReason =
  | 'already-completed'
  | 'already-researching'
  | 'project-active'
  | 'missing-prerequisites'

export interface ResearchAvailability {
  allowed: boolean
  reason?: ResearchBlockReason
  missingPrerequisites: TechnologyId[]
}

export type StartResearchResult =
  | {
      started: true
      state: NationResearchState
    }
  | {
      started: false
      state: NationResearchState
      reason: ResearchBlockReason
      missingPrerequisites: TechnologyId[]
    }

export interface ResearchCompletion {
  nationId: ActiveMajorPowerId
  technologyId: TechnologyId
}

export interface ResearchTurnResult {
  research: NationResearchRecord
  completed: ResearchCompletion[]
}

export function createInitialResearchState(): NationResearchRecord {
  const research = {} as NationResearchRecord
  for (const id of ACTIVE_MAJOR_POWER_IDS) {
    research[id] = { completed: [], active: null }
  }
  return research
}

export function getResearchAvailability(
  state: NationResearchState,
  technologyId: TechnologyId,
): ResearchAvailability {
  if (state.completed.includes(technologyId)) {
    return {
      allowed: false,
      reason: 'already-completed',
      missingPrerequisites: [],
    }
  }

  if (state.active?.technologyId === technologyId) {
    return {
      allowed: false,
      reason: 'already-researching',
      missingPrerequisites: [],
    }
  }

  if (state.active !== null) {
    return {
      allowed: false,
      reason: 'project-active',
      missingPrerequisites: [],
    }
  }

  const definition = getTechnologyDefinition(technologyId)
  const missingPrerequisites = definition.prerequisites.filter(
    (prerequisiteId) => !state.completed.includes(prerequisiteId),
  )

  if (missingPrerequisites.length > 0) {
    return {
      allowed: false,
      reason: 'missing-prerequisites',
      missingPrerequisites,
    }
  }

  return {
    allowed: true,
    missingPrerequisites: [],
  }
}

export function startResearch(
  state: NationResearchState,
  technologyId: TechnologyId,
): StartResearchResult {
  const availability = getResearchAvailability(state, technologyId)

  if (!availability.allowed) {
    return {
      started: false,
      state,
      reason: availability.reason ?? 'missing-prerequisites',
      missingPrerequisites: availability.missingPrerequisites,
    }
  }

  return {
    started: true,
    state: {
      completed: [...state.completed],
      active: {
        technologyId,
        progress: 0,
      },
    },
  }
}

export function progressResearchTurn(
  research: NationResearchRecord,
  pointsByNation: Record<ActiveMajorPowerId, number>,
): ResearchTurnResult {
  const completed: ResearchCompletion[] = []
  const nextResearch = {} as NationResearchRecord
  for (const id of ACTIVE_MAJOR_POWER_IDS) {
    const result = progressNationResearch(research[id], pointsByNation[id])
    nextResearch[id] = result.state
    if (result.completedTechnologyId) {
      completed.push({ nationId: id, technologyId: result.completedTechnologyId })
    }
  }

  return {
    research: nextResearch,
    completed,
  }
}

export function isNationResearchRecord(
  value: unknown,
): value is NationResearchRecord {
  if (!isRecord(value)) {
    return false
  }

  return ACTIVE_MAJOR_POWER_IDS.every((id) => isNationResearchState(value[id]))
}

function progressNationResearch(
  state: NationResearchState,
  researchPoints: number,
): {
  state: NationResearchState
  completedTechnologyId: TechnologyId | null
} {
  if (state.active === null || researchPoints <= 0) {
    return {
      state,
      completedTechnologyId: null,
    }
  }

  const definition = getTechnologyDefinition(state.active.technologyId)
  const progress = Math.min(
    definition.researchCost,
    state.active.progress + researchPoints,
  )

  if (progress >= definition.researchCost) {
    return {
      state: {
        completed: [...state.completed, state.active.technologyId],
        active: null,
      },
      completedTechnologyId: state.active.technologyId,
    }
  }

  return {
    state: {
      completed: [...state.completed],
      active: {
        ...state.active,
        progress,
      },
    },
    completedTechnologyId: null,
  }
}

function isNationResearchState(
  value: unknown,
): value is NationResearchState {
  if (!isRecord(value) || !Array.isArray(value.completed)) {
    return false
  }

  const uniqueCompleted = new Set<TechnologyId>()

  for (const technologyId of value.completed) {
    if (!isTechnologyId(technologyId) || uniqueCompleted.has(technologyId)) {
      return false
    }
    uniqueCompleted.add(technologyId)
  }

  if (value.active === null) {
    return true
  }

  if (!isRecord(value.active) || !isTechnologyId(value.active.technologyId)) {
    return false
  }

  if (uniqueCompleted.has(value.active.technologyId)) {
    return false
  }

  const definition = getTechnologyDefinition(value.active.technologyId)

  return (
    typeof value.active.progress === 'number' &&
    Number.isFinite(value.active.progress) &&
    value.active.progress >= 0 &&
    value.active.progress < definition.researchCost
  )
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}
